import * as React from "react";
import { Activity, ArrowRight, Cookie, Smartphone, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const collectionData = [{
  id: "personal",
  iconName: "user",
  title: "Personal Identification",
  description: "We collect your name and professional contact details when you register. This information is used to manage your account, verify your identity, and send you essential updates about our services."
}, {
  id: "usage",
  iconName: "activity",
  title: "Technical & Usage Logs",
  description: "We monitor platform usage to identify technical issues and improve performance. This anonymous data helps us refine our features and ensure a better experience for everyone."
}, {
  id: "cookies",
  iconName: "cookie",
  title: "Cookie Policy & Tracking",
  description: "We use essential cookies to keep your account secure while you're logged in. We also use optional cookies to remember your settings and preferences for your next visit."
}, {
  id: "device",
  iconName: "smartphone",
  title: "Device & Environment Info",
  description: "We log basic details like your browser type and operating system to ensure our platform works perfectly on your device. This helps us provide a secure and consistent experience across all your hardware."
}];
const iconMap: Record<string, React.ElementType> = {
  user: User,
  activity: Activity,
  cookie: Cookie,
  smartphone: Smartphone
};
export default function DataCollectionUsage() {
  return <section data-section-id="165216" id="data-collection-and-use" className="bg-neutral-950 text-slate-50 py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-sm font-medium text-primary mb-6 tracking-wider">
            TRANS-GOVERNANCE PROTOCOL
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-50 mb-6 drop-shadow-[0_0_15px_rgba(0,255,255,0.15)]">
            Data Collection & Usage
          </h2>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed font-mono">
            We believe in being completely transparent about how we handle your information. 
            Below is a clear breakdown of the data we collect to keep your digital 
            environment secure, reliable, and performing at its best.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {collectionData.map((item, i) => {
          const IconComponent = iconMap[item.iconName];
          return <Card key={item.id} data-index={i} className="border-primary/10 bg-card/40 backdrop-blur-md rounded-xl transition-all duration-500 hover:bg-card/60 hover:shadow-[0_0_30px_-10px_rgba(0,255,255,0.25)] hover:border-primary/60 group">
                <CardHeader className="flex flex-row items-start gap-5 pb-4">
                  <div className="bg-primary/5 p-3 rounded-xl border border-primary/20 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                    <IconComponent className="w-6 h-6 text-primary group-hover:text-primary-foreground stroke-[1.5px]" />
                  </div>
                  <div className="space-y-1">
                    <CardTitle className="text-xl font-bold tracking-tight text-slate-50 group-hover:text-primary transition-colors duration-300">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pl-[4.5rem]">
                  <p className="text-slate-400 leading-relaxed font-sans text-base">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="rounded-full px-8 w-full sm:w-auto bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-shadow">
            <Link to="#">
              View Full Privacy Framework
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <p className="text-sm text-slate-500 text-center sm:text-left font-mono tracking-tight">
            Encrypted via industry-standard TLS protocols.
          </p>
        </div>
      </div>
    </section>;
}